import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { PrimeReactProvider } from 'primereact/api';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Divider } from 'primereact/divider';
import { ProgressBar } from 'primereact/progressbar';
import API_BASE_URL from '../config/api';
// import PhotoUpload from './PhotoUpload'; // Removed - using separate page now

const SubmissionForm = () => {
  const { school } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toastRef = useRef(null);
  
  // Get existing submission data from location state if available
  const existingData = location.state?.submissionData || {};
  
  const [submission, setSubmission] = useState({
    school: school || '',
    name: existingData.name || '',
    instagram_handle: existingData.instagram_handle || '',
    email: existingData.email || '',
    subscribe_newsletter: existingData.subscribe_newsletter || true,
    bio: existingData.bio || ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formProgress, setFormProgress] = useState(0);
  const [schoolName, setSchoolName] = useState('');

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Redirect streamer-university to dedicated flow
  useEffect(() => {
    if (school === 'streamer-university') {
      navigate('/streamer/submit', {
        replace: true,
        state: { submissionData: location.state?.submissionData || existingData }
      });
    }
  }, [school, navigate]);

  // Fetch school name from database
  useEffect(() => {
    const fetchSchoolName = async () => {
      if (!school) return;
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/schools/${school}`);
        const result = await response.json();
        
        if (result.status === 'success') {
          setSchoolName(result.data.school_name);
        } else {
          // Fallback to school code if not found
          setSchoolName(school.toUpperCase());
        }
      } catch (error) {
        console.error('Error fetching school name:', error);
        // Fallback to school code
        setSchoolName(school.toUpperCase());
      }
    };

    fetchSchoolName();
  }, [school]);

  // Calculate initial form progress based on existing data
  useEffect(() => {
    const filledFields = Object.values(submission).filter(val => {
      if (typeof val === 'boolean') return true; // Count boolean fields as filled
      return typeof val === 'string' && val.trim() !== '';
    }).length;
    const totalFields = Object.keys(submission).length;
    setFormProgress((filledFields / totalFields) * 100);
  }, []); // Only run on mount

  const handleInputChange = (field, value) => {
    setSubmission(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Calculate form progress
    const updatedSubmission = { ...submission, [field]: value };
    const filledFields = Object.values(updatedSubmission).filter(val => {
      if (typeof val === 'boolean') return true; // Count boolean fields as filled
      return typeof val === 'string' && val.trim() !== '';
    }).length;
    const totalFields = Object.keys(updatedSubmission).length;
    setFormProgress((filledFields / totalFields) * 100);
  };

  const handleNext = () => {
    // Validate required fields
    if (!submission.name || !submission.bio) {
      toastRef.current?.show({
        severity: 'warn',
        summary: 'Missing Information',
        detail: 'Please fill in your name and bio before continuing.',
        life: 3000
      });
      return;
    }

    // Validate email format if provided
    if (submission.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(submission.email)) {
        toastRef.current?.show({
          severity: 'warn',
          summary: 'Invalid Email',
          detail: 'Please enter a valid email address.',
          life: 3000
        });
        return;
      }
    }

    // Navigate to photo upload page with form data
    navigate(`/submit/${school}/photos`, {
      state: { submissionData: submission }
    });
  };

  const handleBackToLanding = () => {
    navigate('/');
  };

  return (
    <PrimeReactProvider>
      <div className="submission-page">
        <Toast ref={toastRef} />
        
        {/* Header */}
        <header className="form-header">
          <div className="header-content">
            <Button
              icon="pi pi-arrow-left"
              label="Back"
              className="back-button"
              onClick={handleBackToLanding}
            />
            <div className="header-title">
              <p>{schoolName || 'Your University'}</p>
            </div>
          </div>
        </header>

        {/* Progress Bar */}
        <div className="progress-section">
          <div className="container">
            <div className="progress-info">
              <span>Step 1 of 3: Profile Info</span>
              <span>{Math.round(formProgress)}% Complete</span>
            </div>
            <ProgressBar 
              value={formProgress} 
              className="form-progress"
              showValue={false}
            />
          </div>
        </div>

        {/* Main Form */}
        <main className="form-main">
          <div className="container">
            <Card className="form-card">
              <div className="form-header-info">
                <h2>Tell Us About Yourself</h2>
              </div>

              <div className="submission-form">
                <div className="form-grid">
                  <div className="form-field">
                    <label htmlFor="name">Full Name *</label>
                    <InputText
                      id="name"
                      value={submission.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Your full name"
                      required
                      className="w-full"
                    />
                  </div>
                  
                  <div className="form-field">
                    <label htmlFor="instagram_handle">Instagram Handle *</label>
                    <InputText
                      id="instagram_handle"
                      value={submission.instagram_handle}
                      onChange={(e) => handleInputChange('instagram_handle', e.target.value)}
                      placeholder="@username"
                      className="w-full"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="none"
                      spellCheck="false"
                    />
                  </div>
                  
                  <div className="form-field">
                    <label htmlFor="email">Email *</label>
                    <InputText
                      id="email"
                      type="email"
                      value={submission.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="email@example.com"
                      className="w-full"
                    />
                  </div>
                  
                  <div className="form-field" style={{ justifyContent: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.0rem' }}>
                      <input
                        type="checkbox"
                        id="subscribe_newsletter"
                        name="subscribe_newsletter"
                        checked={submission.subscribe_newsletter}
                        onChange={(e) => handleInputChange('subscribe_newsletter', e.target.checked)}
                        style={{ 
                          width: '17px', 
                          height: '17px', 
                          cursor: 'pointer',
                          accentColor: '#6E8CFB',
                        }}
                      />
                      <label 
                        htmlFor="subscribe_newsletter" 
                        style={{ cursor: 'pointer', marginBottom: 0, fontWeight: 'normal' }}
                      >
                        Subscribe to newsletter
                      </label>
                    </div>
                  </div>
                  
                  <div className="form-field full-width">
                    <label htmlFor="bio">Bio *</label>
                    <InputTextarea
                      id="bio"
                      value={submission.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      placeholder="Share anything you'd like potential friends to know about you..."
                      rows={4}
                      className="w-full"
                      required
                    />
                    <small className="field-help">This will be featured in your Instagram post to help classmates get to know you!</small>
                  </div>
                </div>
                
                <Divider />
                
                <div className="form-actions">
                  <Button
                    label="Next: Photos"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    onClick={handleNext}
                    className="next-button"
                    disabled={!submission.name || !submission.bio || !submission.email || !submission.instagram_handle}
                  />
                  <p className="form-note">
                    Complete your profile information to continue to photo upload.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </main>
      </div>
    </PrimeReactProvider>
  );
};

export default SubmissionForm; 